/**
 * @fileoverview Syndicator Showdown — Equity Target Helpers
 * @module utils/equityPricing
 *
 * Small pure-function layer on top of @ss/shared's calculatePurchaseCost.
 * These helpers answer "what equity % gets me to/near a target spend?" —
 * needed by the BuyModal quick-buy buttons (Race Floor / Race Avg / 100% /
 * Max Safe) and by the Quick Pick auto-fill strategies.
 *
 * IMPORTANT: cost is ALWAYS derived via calculatePurchaseCost (the tiered
 * pricing curve — T1..T5 tranches). Nothing here reimplements pricing —
 * per CodingStandards.md, calculatePurchaseCost is the single source of
 * truth for cost-from-equity. A naive `equity * basePrice` linear formula
 * would understate cost above 30% equity and desync from every other
 * screen (ReviewSlip, BetSlip, RaceCard) that already uses this function.
 */

import { calculatePurchaseCost } from '@ss/shared';

/** Total cost to hold `equityPct` of a runner from scratch (0%). */
export function costForEquity(basePrice, equityPct) {
  if (!basePrice || !equityPct || equityPct <= 0) return 0;
  return calculatePurchaseCost(0, Math.round(equityPct), basePrice);
}

/** Additional cost to move from `fromPct` to `toPct` equity. */
export function deltaCostBetween(basePrice, fromPct, toPct) {
  return costForEquity(basePrice, toPct) - costForEquity(basePrice, fromPct);
}

/**
 * Smallest whole-percent target (>= fromPct, <= maxPct) whose additional
 * cost from fromPct meets or exceeds `additionalSpend`. Used for "round UP
 * to meet a floor/budget" buttons. Returns fromPct unchanged if there's
 * nothing left to spend, and maxPct if the spend can never be met.
 */
export function findEquityToMeetSpend(basePrice, fromPct, additionalSpend, maxPct = 100) {
  const from = Math.max(0, Math.round(fromPct));
  if (!basePrice || additionalSpend == null || additionalSpend <= 0) return from;
  const fromCost = costForEquity(basePrice, from);
  for (let pct = from + 1; pct <= maxPct; pct++) {
    if (costForEquity(basePrice, pct) - fromCost >= additionalSpend) return pct;
  }
  return maxPct;
}

/**
 * Largest whole-percent target (>= fromPct, <= maxPct) whose additional
 * cost from fromPct does NOT exceed `availableSpend`. Used for "round DOWN
 * to not exceed a budget" buttons (Race Avg, 100%, Max Safe).
 */
export function findMaxEquityWithinBudget(basePrice, fromPct, availableSpend, maxPct = 100) {
  const from = Math.max(0, Math.round(fromPct));
  if (!basePrice || availableSpend == null || availableSpend <= 0) return from;
  const fromCost = costForEquity(basePrice, from);
  let best = from;
  for (let pct = from + 1; pct <= maxPct; pct++) {
    if (costForEquity(basePrice, pct) - fromCost <= availableSpend) {
      best = pct;
    } else {
      break; // cost per additional point never decreases — safe to stop
    }
  }
  return best;
}

// ─── BuyModal quick-buy target calculators ─────────────────────────────────
//
// Pure functions so the "does this button account for what other runners in
// the race already spent?" logic can be unit-tested without rendering
// BuyModal. All four follow the same shape: given the runner's basePrice,
// what the player already owns (currentOwnedPct), and race/wallet context,
// return the equity % the button should set as draftTargetPct.
//
// Every function is guaranteed to return a value >= currentOwnedPct (never
// suggests "selling" — that flow is SellEquityModal's job) and <= maxPct.

/**
 * "Race Floor" — tops this race up to its minimum spend requirement using
 * ONLY this runner, crediting whatever OTHER runners in the race have
 * already spent. Returns currentOwnedPct unchanged if other runners already
 * meet the floor on their own.
 */
export function calcRaceFloorTarget({ basePrice, currentOwnedPct, currentRaceSpend, raceFloor, maxPct = 100 }) {
  const costAtCurrent        = costForEquity(basePrice, currentOwnedPct);
  const spentByOthersInRace  = Math.max(0, currentRaceSpend - costAtCurrent);
  const neededTotalForRace   = Math.max(0, raceFloor - spentByOthersInRace);
  const additionalNeeded     = Math.max(0, neededTotalForRace - costAtCurrent);
  if (additionalNeeded <= 0) return Math.round(currentOwnedPct);
  return findEquityToMeetSpend(basePrice, currentOwnedPct, additionalNeeded, maxPct);
}

/**
 * "Race Avg" — spends up to the race's average spend using this runner,
 * crediting other runners' existing spend, WITHOUT exceeding the average
 * unless the player already owns more than that would imply (in which case
 * we never suggest reducing equity — clamped to currentOwnedPct).
 */
export function calcRaceAvgTarget({ basePrice, currentOwnedPct, currentRaceSpend, avgSpendPerRace, maxPct = 100 }) {
  const costAtCurrent       = costForEquity(basePrice, currentOwnedPct);
  const spentByOthersInRace = Math.max(0, currentRaceSpend - costAtCurrent);
  const budgetToReachAvg    = Math.max(0, avgSpendPerRace - spentByOthersInRace);
  const idealFromScratch    = findMaxEquityWithinBudget(basePrice, 0, budgetToReachAvg, maxPct);
  return Math.max(idealFromScratch, Math.round(currentOwnedPct));
}

/** "100%" — max affordable equity given remaining wallet, capped at maxPct. */
export function calcMaxTarget({ basePrice, currentOwnedPct, walletRemaining, maxPct = 100 }) {
  return findMaxEquityWithinBudget(basePrice, currentOwnedPct, walletRemaining, maxPct);
}

/** "Max Safe" — like 100%, but reserves wallet for other races' floor shortfalls first. */
export function calcMaxSafeTarget({ basePrice, currentOwnedPct, walletRemaining, reservedForFloors = 0, maxPct = 100 }) {
  const available = Math.max(0, walletRemaining - reservedForFloors);
  return findMaxEquityWithinBudget(basePrice, currentOwnedPct, available, maxPct);
}
